import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Scan, Users, Shield, Globe } from 'lucide-react';

const LoginPage = () => {
  const { login, register, error } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('ja');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const t = useTranslation(language);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');

    const result = isLogin
      ? await login(email, password)
      : await register(email, password, name, language);

    if (!result.success) {
      setLocalError(result.error);
    }
    setLoading(false);
  };

  const features = [
    { icon: FileText, title: language === 'ja' ? 'OCR自動整理' : 'Auto OCR Organization', desc: language === 'ja' ? 'アップロードだけで分類・タイトル生成' : 'Upload and auto-classify' },
    { icon: Scan, title: language === 'ja' ? '名刺自動CRM化' : 'Business Card to CRM', desc: language === 'ja' ? '名刺をスキャンして連絡先自動登録' : 'Scan cards to auto-register contacts' },
    { icon: Users, title: language === 'ja' ? '統合ビュー' : 'Unified View', desc: language === 'ja' ? '人・書類・映像・履歴を1画面で管理' : 'Manage people, docs, media in one view' },
    { icon: Shield, title: language === 'ja' ? '証跡管理' : 'Audit Trail', desc: language === 'ja' ? '全操作ログ・法対応' : 'Complete operation logs, compliance' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Features */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/1089438/pexels-photo-1089438.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}
        />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-4 mb-4">
            <img src="/logo.png" alt="RK File Control" className="w-16 h-16 object-contain" />
            <h1 className="text-4xl font-bold text-white text-glow">RK File Control</h1>
          </div>
          <p className="text-muted-foreground text-lg mb-12">
            {language === 'ja' ? '書類管理 + CRM + 証跡統合システム' : 'Document + CRM + Audit System'}
          </p>
          
          <div className="space-y-6">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-4 p-4 card-cyber border-glow">
                <div className="p-2 bg-primary/10 rounded-sm">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <Card className="w-full max-w-md card-cyber border-glow">
          <CardHeader className="space-y-1 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src="/logo.png" alt="RK File Control" className="w-12 h-12 object-contain" />
              <CardTitle className="text-2xl text-glow" data-testid="login-title">
                {t.auth.loginTitle}
              </CardTitle>
            </div>
            <CardDescription className="text-muted-foreground">
              {t.auth.loginSubtitle}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Language Selector */}
            <div className="flex justify-end mb-4">
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-24 h-8 input-cyber" data-testid="language-selector">
                  <Globe className="w-4 h-4 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ja">日本語</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm">{t.auth.name}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-cyber"
                    placeholder={language === 'ja' ? '山田太郎' : 'John Doe'}
                    required={!isLogin}
                    data-testid="register-name-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm">{t.auth.email}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-cyber"
                  placeholder="email@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm">{t.auth.password}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-cyber"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>

              {(localError || error) && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm text-destructive text-sm" data-testid="auth-error">
                  {localError || error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading}
                data-testid="auth-submit-button"
              >
                {loading ? t.common.loading : (isLogin ? t.auth.loginButton : t.auth.registerButton)}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">
                {isLogin ? t.auth.noAccount : t.auth.hasAccount}
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setLocalError('');
                }}
                className="ml-2 text-primary hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? t.auth.register : t.auth.login}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
